interface LoadMoreResponse {
    success: boolean;
    html?: string;
    displayedCount?: number;
    totalGames?: number;
    currentPage?: number;
    lastPage?: number;
    message?: string;
}

interface GameFilters {
    [key: string]: string | number;
}

declare global {
    interface Window {
        loadMoreGames: LoadMoreGames;
        lazyLoadInstance?: {
            update(): void;
        };
    }
}

class LoadMoreGames {
    private loadMoreBtn: HTMLButtonElement | null;
    private gamesContainer: HTMLElement | null;
    private displayedCountEl: HTMLElement | null;
    private totalCountEl: HTMLElement | null;
    private progressBarEl: HTMLElement | null;
    private abortController: AbortController | null = null;

    private currentPage: number = 1;
    private lastPage: number = 1;
    private isLoading: boolean = false;
    private categoryId: string = '';
    private filters: GameFilters = {};

    constructor() {
        this.loadMoreBtn = document.getElementById('load-more-btn') as HTMLButtonElement;
        this.gamesContainer = document.getElementById('games-container');
        this.displayedCountEl = document.getElementById('displayed-count');
        this.totalCountEl = document.getElementById('total-count');
        this.progressBarEl = document.getElementById('load-more-progress-bar');

        this.init();
    }

    private init(): void {
        if (!this.loadMoreBtn) return;

        this.currentPage = parseInt(this.loadMoreBtn.dataset.currentPage || '1', 10);
        this.lastPage = parseInt(this.loadMoreBtn.dataset.lastPage || '1', 10);
        this.categoryId = this.loadMoreBtn.dataset.category || '';

        try {
            const parsed = JSON.parse(this.loadMoreBtn.dataset.filters || '{}');
            this.filters = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (e) {
            this.filters = {};
        }

        this.loadMoreBtn.addEventListener('click', () => this.loadMore());

        this.updateDisplayedCount();
        this.updateProgressBar();
    }

    private async loadMore(): Promise<void> {
        if (this.isLoading) return;

        this.setLoadingState(true);

        try {
            const nextPage = this.currentPage + 1;
            const response = await this.fetchMoreGames(nextPage);

            if (!response.success) {
                this.showError(response.message || 'Failed to load more games');
                return;
            }

            this.appendGames(response.html || '');
            this.currentPage = nextPage;

            if (response.lastPage !== undefined) {
                this.lastPage = response.lastPage;
            }
            if (response.totalGames !== undefined && this.totalCountEl) {
                this.totalCountEl.textContent = String(response.totalGames);
            }

            this.updateDisplayedCount(response.displayedCount);

            this.removeNoMoreGamesMessage();
            if (this.currentPage >= this.lastPage) {
                this.hideLoadMoreButton();
            } else {
                this.showLoadMoreButton();
            }
        } catch (error) {
            console.error('Load more error:', error);
            this.showError('Failed to load more games. Please try again.');
        } finally {
            this.setLoadingState(false);
        }
    }

    private async fetchMoreGames(page: number): Promise<LoadMoreResponse> {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const currentUrl = new URL(window.location.href);

        const datasetSlug = this.loadMoreBtn?.dataset.category;
        const hasDatasetSlug = Boolean(datasetSlug && datasetSlug.trim().length > 0);

        let loadMoreUrl: URL;

        if (hasDatasetSlug) {
            const casinoRoot = this.getCasinoRootPath(currentUrl);
            loadMoreUrl = new URL(`${casinoRoot}/${encodeURIComponent(datasetSlug!)}/load-more`, currentUrl.origin);
        } else {
            loadMoreUrl = new URL(`${currentUrl.pathname.replace(/\/$/, '')}/load-more`, currentUrl.origin);
        }

        loadMoreUrl.searchParams.set('page', page.toString());

        currentUrl.searchParams.forEach((value, key) => {
            loadMoreUrl.searchParams.set(key, value);
        });

        const response = await fetch(loadMoreUrl.toString(), {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                Accept: 'application/json',
            },
            signal: this.abortController.signal,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    private getCasinoRootPath(currentUrl: URL): string {
        const casinoUrl = (window as any).CASINO_URL;

        if (typeof casinoUrl === 'string' && casinoUrl.trim() !== '') {
            try {
                const parsed = new URL(casinoUrl, currentUrl.origin);
                const normalized = parsed.pathname.replace(/\/$/, '');
                return normalized === '' ? '' : normalized;
            } catch {
                // Fall back to path detection below.
            }
        }

        const basePath = currentUrl.pathname.replace(/\/$/, '');
        const match = basePath.match(/^(.*\/casino)(?:\/.*)?$/);
        return match?.[1] || '';
    }

    private appendGames(html: string): void {
        if (!html || !this.gamesContainer) return;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const gameElements = tempDiv.children;
        while (gameElements.length > 0) {
            this.gamesContainer.appendChild(gameElements[0]);
        }

        this.initializeNewGames();
    }

    private replaceGames(html: string): void {
        if (!this.gamesContainer) return;
        this.gamesContainer.innerHTML = html || '';
        this.initializeNewGames();
    }

    private initializeNewGames(): void {
        this.initializeLazyLoading();
        document.dispatchEvent(
            new CustomEvent('gamesLoaded', {
                detail: { container: this.gamesContainer },
            })
        );
    }

    private initializeLazyLoading(): void {
        if (!this.gamesContainer) return;

        const newImages = this.gamesContainer.querySelectorAll('.lazyLoad:not([data-lazy-initialized])');
        if (newImages.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const img = entry.target as HTMLImageElement;
                        const src = img.getAttribute('data-src');

                        if (src) {
                            img.src = src;
                            img.classList.remove('lazyLoad');
                            img.setAttribute('data-lazy-initialized', 'true');
                            observer.unobserve(img);
                        }
                    }
                });
            },
            { rootMargin: '50px 0px', threshold: 0.01 }
        );

        newImages.forEach((img) => observer.observe(img));
    }

    private updateDisplayedCount(newCount?: number): void {
        if (!this.displayedCountEl || !this.totalCountEl) return;

        const total = parseInt(this.totalCountEl.textContent || '0', 10);

        let displayed: number;
        if (newCount !== undefined) {
            displayed = newCount;
        } else {
            displayed = this.gamesContainer?.children.length || 0;
        }

        this.displayedCountEl.textContent = Math.min(displayed, total).toString();
        this.updateProgressBar();
    }

    private updateProgressBar(): void {
        if (!this.progressBarEl || !this.displayedCountEl || !this.totalCountEl) return;

        const displayed = parseInt(this.displayedCountEl.textContent || '0', 10);
        const total = parseInt(this.totalCountEl.textContent || '0', 10);

        const percent = total > 0 ? Math.min(100, Math.round((displayed / total) * 100)) : 0;
        this.progressBarEl.style.width = `${percent}%`;
    }

    private setLoadingState(loading: boolean): void {
        if (!this.loadMoreBtn) return;

        this.isLoading = loading;

        if (loading) {
            this.loadMoreBtn.classList.add('loading');
            this.loadMoreBtn.disabled = true;
        } else {
            this.loadMoreBtn.classList.remove('loading');
            this.loadMoreBtn.disabled = false;
        }
    }

    private showLoadMoreButton(): void {
        if (!this.loadMoreBtn) return;
        this.loadMoreBtn.style.display = '';
    }

    private hideLoadMoreButton(): void {
        if (!this.loadMoreBtn) return;

        this.loadMoreBtn.style.display = 'none';

        const section = this.loadMoreBtn.closest('.load-more-section');
        if (section && !section.querySelector('.no-more-games')) {
            const message = document.createElement('div');
            message.className = 'no-more-games';
            message.style.cssText = 'color: #fff; opacity: 0.6; font-size: 14px; margin-top: 10px;';
            message.textContent = 'All games loaded';
            section.appendChild(message);
        }
    }

    private removeNoMoreGamesMessage(): void {
        const section = this.loadMoreBtn?.closest('.load-more-section');
        section?.querySelector('.no-more-games')?.remove();
    }

    private showError(message: string): void {
        if (!this.loadMoreBtn) return;

        let errorEl = document.querySelector('.load-more-error') as HTMLElement;
        if (!errorEl) {
            errorEl = document.createElement('div');
            errorEl.className = 'load-more-error';
            errorEl.style.cssText = 'color: #ff6b6b; font-size: 14px; margin-top: 10px; text-align: center;';
            this.loadMoreBtn.parentNode?.appendChild(errorEl);
        }

        errorEl.textContent = message;

        setTimeout(() => {
            errorEl.parentNode?.removeChild(errorEl);
        }, 5000);
    }

    public updateFilters(newFilters: GameFilters): void {
        Object.keys(newFilters).forEach((key) => {
            if (newFilters[key] === '' || newFilters[key] === null || newFilters[key] === undefined) {
                delete this.filters[key];
            } else {
                this.filters[key] = newFilters[key];
            }
        });

        if (this.loadMoreBtn) {
            this.loadMoreBtn.dataset.filters = JSON.stringify(this.filters);
        }
    }

    public async resetAndLoadFirstPage(): Promise<void> {
        this.setLoadingState(true);

        try {
            const response = await this.fetchMoreGames(1);

            if (!response.success) {
                this.showError(response.message || 'Failed to load games');
                return;
            }

            this.replaceGames(response.html || '');

            this.currentPage = 1;
            if (response.lastPage !== undefined) {
                this.lastPage = response.lastPage;
            }

            if (response.totalGames !== undefined && this.totalCountEl) {
                this.totalCountEl.textContent = String(response.totalGames);
            }

            if (this.loadMoreBtn) {
                this.loadMoreBtn.dataset.currentPage = String(this.currentPage);
                this.loadMoreBtn.dataset.lastPage = String(this.lastPage);
            }

            this.updateDisplayedCount(response.displayedCount);

            this.removeNoMoreGamesMessage();

            if (this.currentPage >= this.lastPage) {
                this.hideLoadMoreButton();
            } else {
                this.showLoadMoreButton();
            }
        } catch (e: any) {
            if (e.name === 'AbortError') {
                return;
            }
            console.error(e);
            this.showError('Failed to load games. Please try again.');
        } finally {
            this.setLoadingState(false);
        }
    }
}

const initLoadMoreGames = () => {
    window.loadMoreGames = new LoadMoreGames();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoadMoreGames);
} else {
    initLoadMoreGames();
}

export default LoadMoreGames;
